/**
 * 登録機体一覧ページ - Neo-Futuristic
 */

import React, { useState, useEffect, useCallback } from 'react';
import { dipsApi } from '../services/api';

// 機体種類のラベル
const AIRCRAFT_TYPE_LABELS = {
  '1': '回転翼（マルチローター）',
  '2': '回転翼（ヘリコプター）',
  '3': '固定翼',
  '4': '固定翼（VTOL）',
};

// 所有者区分のラベル
const OWNER_CLASSIFICATION_LABELS = {
  '1': '個人',
  '9': '法人',
};

// ヘルパー関数
const getAircraftInfo = (aircraft) => aircraft?.aircraft_information || aircraft;
const getOwnerInfo   = (aircraft) => aircraft?.owner_information;
const getUserInfo    = (aircraft) => aircraft?.user_information;

// 日付フォーマット
const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  try { return new Date(dateStr).toLocaleDateString('ja-JP'); } catch { return dateStr; }
};

// 日時を "yyyyMMdd HHmm" 形式にフォーマット
const formatDipsDateTime = (date) => {
  const y   = date.getFullYear();
  const m   = String(date.getMonth() + 1).padStart(2, '0');
  const d   = String(date.getDate()).padStart(2, '0');
  const h   = String(date.getHours()).padStart(2, '0');
  const min = String(date.getMinutes()).padStart(2, '0');
  return `${y}${m}${d} ${h}${min}`;
};


// Material Symbol
function MSIcon({ name, className = 'text-xl' }) {
  return <span className={`material-symbols-outlined leading-none select-none ${className}`}>{name}</span>;
}

// ステータスバッジ
function StatusBadge({ status }) {
  const isActive  = status === '1';
  const isExpired = status === '2';
  const label     = isActive ? '有効' : isExpired ? '期限切れ' : '抹消済';
  const color     = isActive
    ? 'border-secondary/50 text-secondary'
    : isExpired
    ? 'border-error/50 text-error'
    : 'border-outline/50 text-outline';
  const dot = isActive
    ? 'bg-secondary'
    : isExpired
    ? 'bg-error'
    : 'bg-outline';

  return (
    <span className={`px-2 py-0.5 border ${color} text-[12px] font-label-caps rounded-full flex items-center gap-1 whitespace-nowrap`}>
      <span className={`w-1.5 h-1.5 ${dot} rounded-full ${isActive ? 'animate-pulse' : ''}`} />
      {label}
    </span>
  );
}

// Neo-Futuristic ボタン
function HudButton({ children, icon, iconClassName, variant = 'primary', size = 'md', loading, onClick, disabled, type = 'button' }) {
  const base = 'rounded-none font-label-caps tracking-[0.15em] flex items-center justify-center gap-2 transition-all active:scale-[0.98] disabled:opacity-40 disabled:cursor-not-allowed';
  const sizes = { sm: 'h-8 px-3 text-sm', md: 'h-10 px-4 text-[15px]', wide: 'h-10 px-8 text-[15px]', lg: 'h-14 px-8 text-base font-extrabold' };
  const variants = {
    primary:   'bg-primary text-on-primary hover:shadow-[0_0_20px_rgba(162,201,255,0.6)]',
    secondary: 'bg-transparent border border-primary/50 text-primary hover:border-primary hover:bg-primary/10',
    danger:    'bg-transparent border border-error/50 text-error hover:border-error hover:bg-error/10',
  };
  const defaultIconClass = size === 'lg' ? 'text-2xl' : 'text-base';
  return (
    <button type={type} onClick={onClick} disabled={disabled || loading} className={`${base} ${sizes[size]} ${variants[variant]}`}>
      {loading
        ? <><svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>PROCESSING...</>
        : <>{icon && <MSIcon name={icon} className={iconClassName ?? defaultIconClass} />}{children}</>
      }
    </button>
  );
}

// エラー / 情報アラート
function HudAlert({ message, variant = 'error' }) {
  if (!message) return null;
  const styles = {
    error:   'border-error/50 bg-error-container/30 text-error',
    warning: 'border-tertiary/50 bg-tertiary-container/20 text-tertiary',
    success: 'border-secondary/50 bg-secondary-container/20 text-secondary',
  };
  const icons = { error: 'warning', warning: 'info', success: 'check_circle' };
  return (
    <div className={`border ${styles[variant]} px-4 py-3 flex items-center gap-3`}>
      <MSIcon name={icons[variant]} className="text-base flex-shrink-0" />
      <span className="font-label-caps text-[15px] tracking-wider">{message}</span>
    </div>
  );
}

// ローディングスピナー
function HudSpinner({ label }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 gap-4">
      <div className="relative w-12 h-12">
        <div className="absolute inset-0 border-2 border-primary/20" />
        <div className="absolute inset-0 border-t-2 border-primary animate-spin" />
        <div className="absolute inset-2 border border-primary/10" />
      </div>
      {label && <span className="font-label-caps text-sm text-primary/60 tracking-[0.2em]">{label}</span>}
    </div>
  );
}

// 詳細行
function DetailRow({ label, value }) {
  return (
    <div className="flex items-start gap-2">
      <span className="font-label-caps text-sm text-slate-500 tracking-wider w-28 flex-shrink-0">{label}</span>
      <span className="font-body-sm text-base text-on-surface">{value || '—'}</span>
    </div>
  );
}

// モーダル（hud-rise / hud-fold アニメーション付き）
function HudModal({ isOpen, onClose, title, children }) {
  const [mounted, setMounted]   = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setMounted(true);
      setIsClosing(false);
    } else if (mounted) {
      setIsClosing(true);
    }
  }, [isOpen]); // eslint-disable-line

  if (!mounted) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center p-4">
      <div className={`absolute inset-0 bg-slate-950/50 backdrop-blur-sm ${isClosing ? 'hud-backdrop-fold' : 'hud-backdrop-rise'}`} onClick={onClose} />
      <div
        className={`${isClosing ? 'hud-fold' : 'hud-rise'} relative w-full max-w-2xl glass-panel shadow-[0_-20px_50px_rgba(0,104,183,0.2)] flex flex-col`}
        onAnimationEnd={() => { if (isClosing) setMounted(false); }}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-blue-900/40 flex-shrink-0">
          <span className="font-label-caps text-[15px] text-blue-400 tracking-[0.2em] uppercase">{title}</span>
          <button onClick={onClose} className="p-1.5 border border-blue-900/50 hover:bg-blue-900/30 transition-colors">
            <MSIcon name="close" className="text-base text-slate-400" />
          </button>
        </div>
        <div className="overflow-auto p-5 flex-1">{children}</div>
        <div className="flex justify-center py-2 border-t border-blue-900/20 flex-shrink-0">
          <div className="w-10 h-0.5 bg-blue-900/50 rounded-full" />
        </div>
      </div>
    </div>
  );
}

// 機体詳細モーダル（4タブ + hud-rise / fold アニメーション）
const AIRCRAFT_TABS = ['BASIC_INFO', 'REMOTE_ID', 'SPECS', 'RAW_JSON'];

function AircraftDetailModal({ aircraft, onClose }) {
  const [mounted, setMounted]               = useState(false);
  const [isClosing, setIsClosing]           = useState(false);
  const [activeTab, setActiveTab]           = useState('BASIC_INFO');
  const [displayAircraft, setDisplayAircraft] = useState(null);
  const isOpen = !!aircraft;

  useEffect(() => {
    if (isOpen) {
      setDisplayAircraft(aircraft);
      setMounted(true);
      setIsClosing(false);
      setActiveTab('BASIC_INFO');
    } else if (mounted) {
      setIsClosing(true);
    }
  }, [isOpen]); // eslint-disable-line

  if (!mounted) return null;

  const info = getAircraftInfo(displayAircraft);

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center p-4">
      <div className={`absolute inset-0 bg-slate-950/50 backdrop-blur-sm ${isClosing ? 'hud-backdrop-fold' : 'hud-backdrop-rise'}`} onClick={onClose} />
      <div
        className={`${isClosing ? 'hud-fold' : 'hud-rise'} relative w-full max-w-[800px] glass-panel shadow-[0_-20px_50px_rgba(0,104,183,0.2)] flex flex-col`}
        onAnimationEnd={() => { if (isClosing) setMounted(false); }}
      >
        {/* ヘッダー */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-blue-900/40 flex-shrink-0">
          <span className="font-label-caps text-[15px] text-blue-400 tracking-[0.2em] uppercase">
            AIRCRAFT_DETAIL // {info?.registration_code || ''}
          </span>
          <button onClick={onClose} className="p-1.5 border border-blue-900/50 hover:bg-blue-900/30 transition-colors">
            <MSIcon name="close" className="text-base text-slate-400" />
          </button>
        </div>

        {/* タブバー */}
        <div className="flex border-b border-blue-900/40 flex-shrink-0">
          {AIRCRAFT_TABS.map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-5 py-2.5 font-label-caps text-sm tracking-[0.15em] transition-colors border-b-2 -mb-px
                ${activeTab === tab
                  ? 'border-primary text-primary'
                  : 'border-transparent text-slate-500 hover:text-slate-300'
                }`}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* コンテンツ（固定高さ） */}
        <div className={`p-5 flex-shrink-0 h-[320px] ${activeTab === 'RAW_JSON' ? 'overflow-auto' : 'overflow-hidden'}`}>

          {/* BASIC_INFO */}
          {activeTab === 'BASIC_INFO' && (
            <div className="grid grid-cols-2 gap-3">
              <DetailRow label="登録記号" value={info.registration_code} />
              <DetailRow label="製造番号" value={info.manufacturing_number} />
              <DetailRow label="機体種類" value={AIRCRAFT_TYPE_LABELS[info.aircraft_type] || info.aircraft_type} />
              <DetailRow label="メーカー" value={info.manufacturer_jpn || info.manufacturer_eng} />
              <DetailRow label="モデル"   value={info.model_jpn || info.model_eng} />
              <DetailRow label="登録日"   value={formatDate(info.effectiveness_period_self)} />
              <DetailRow label="有効期限" value={formatDate(info.effectiveness_period_to)} />
              <DetailRow label="最終更新" value={formatDate(info.last_update_date)} />
            </div>
          )}

          {/* REMOTE_ID */}
          {activeTab === 'REMOTE_ID' && (
            <div className="grid grid-cols-2 gap-3">
              <DetailRow label="RID製造番号" value={info.rid_manufacturing_number} />
              <DetailRow label="RIDメーカー" value={info.rid_manufacturer_jpn || info.rid_manufacturer_eng} />
              <DetailRow label="RID型式"     value={info.rid_model_jpn || info.rid_model_eng} />
              <DetailRow label="RID種類"     value={info.rid_type === '1' ? '内蔵型' : info.rid_type === '2' ? '外付け型' : info.rid_type} />
            </div>
          )}

          {/* SPECS */}
          {activeTab === 'SPECS' && (
            <div className="grid grid-cols-3 gap-2">
              {[
                { label: 'WEIGHT_KG',    value: info.aircraft_weight        ? `${info.aircraft_weight} kg`        : '—' },
                { label: 'MAX_TKOFF_KG', value: info.maximum_takeoff_weight ? `${info.maximum_takeoff_weight} kg` : '—' },
                { label: 'LENGTH_M',     value: info.aircraft_length        ? `${info.aircraft_length} m`         : '—' },
                { label: 'WIDTH_M',      value: info.aircraft_width         ? `${info.aircraft_width} m`          : '—' },
                { label: 'HEIGHT_M',     value: info.aircraft_height        ? `${info.aircraft_height} m`         : '—' },
              ].map(({ label, value }) => (
                <div key={label} className="bg-slate-900/50 p-3 border border-blue-900/20">
                  <p className="font-label-caps text-[12px] text-slate-500">{label}</p>
                  <p className="font-label-caps text-base text-blue-400 font-bold mt-1">{value}</p>
                </div>
              ))}
            </div>
          )}

          {/* RAW_JSON */}
          {activeTab === 'RAW_JSON' && (
            <div className="bg-slate-950 border border-blue-900/20 p-4 h-full overflow-auto">
              <pre className="font-body-sm text-[15px] text-slate-500 whitespace-pre-wrap">
                {JSON.stringify(displayAircraft, null, 2)}
              </pre>
            </div>
          )}
        </div>

        {/* 下部装飾 */}
        <div className="flex justify-center py-2 border-t border-blue-900/20 flex-shrink-0">
          <div className="w-10 h-0.5 bg-blue-900/50 rounded-full" />
        </div>
      </div>
    </div>
  );
}

// ========== メインコンポーネント ==========
export default function DipsAircrafts() {
  const [dipsAircrafts, setDipsAircrafts]         = useState(null);
  const [dipsLoading, setDipsLoading]             = useState(false);
  const [dipsError, setDipsError]                 = useState(null);
  const [dipsAuthenticated, setDipsAuthenticated] = useState(dipsApi.isAuthenticated());
  const [selectedAircraft, setSelectedAircraft]   = useState(null);

  // 飛行計画
  const [flightPlans, setFlightPlans]                         = useState([]);
  const [flightPlansLoading, setFlightPlansLoading]           = useState(false);
  const [flightPlansError, setFlightPlansError]               = useState(null);
  const [selectedFlightPlan, setSelectedFlightPlan]           = useState(null);
  const [flightPlanAuthenticated, setFlightPlanAuthenticated] = useState(dipsApi.isFlightPlanAuthenticated());
  const [flightPlanAuthLoading, setFlightPlanAuthLoading]     = useState(false);
  const [searchDate, setSearchDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [scanMode, setScanMode]     = useState(false);
  const [scanProgress, setScanProgress] = useState('');
  const [scanResults, setScanResults]   = useState([]);

  // 飛行計画登録フォーム
  const [showReportForm, setShowReportForm] = useState(false);
  const [reportLoading, setReportLoading]   = useState(false);
  const [reportResult, setReportResult]     = useState(null);
  const [reportForm, setReportForm] = useState({
    flightDate:   new Date().toISOString().split('T')[0],
    startTime:    '09:00',
    endTime:      '17:00',
    latitude:     '35.6895',
    longitude:    '139.6917',
    radius:       '500',
    purpose:      '空撮',
    maxAltitude:  '150',
    remarks:      '',
  });

  // DIPS機体情報取得
  const fetchDipsAircrafts = useCallback(async () => {
    if (!dipsApi.isAuthenticated()) { setDipsAuthenticated(false); return; }
    setDipsLoading(true);
    setDipsError(null);
    try {
      const data = await dipsApi.getAircrafts();
      setDipsAircrafts(data);
      setDipsAuthenticated(true);
    } catch (err) {
      setDipsError(err.message);
      if (err.message.includes('認証')) setDipsAuthenticated(false);
    } finally {
      setDipsLoading(false);
    }
  }, []);

  // DIPS飛行計画取得
  const fetchFlightPlans = useCallback(async () => {
    if (!dipsApi.isFlightPlanAuthenticated()) return;
    setFlightPlansLoading(true);
    setFlightPlansError(null);
    try {
      const [year, month, day] = searchDate.split('-').map(Number);
      const searchParams = {
        features:      { type: 'Circle', center: [139.6917, 35.6895], radius: 500000 },
        startTime:     formatDipsDateTime(new Date(year, month - 1, day, 0, 0)),
        finishTime:    formatDipsDateTime(new Date(year, month - 1, day, 23, 59)),
        allFlightPlan: '1',
      };
      const data  = await dipsApi.getFlightPlans(searchParams);
      const plans = Array.isArray(data) ? data : data.flight_plans || data.flightPlans || data.items || data.results || [];
      setFlightPlans(plans);
    } catch (err) {
      setFlightPlansError(err.message);
    } finally {
      setFlightPlansLoading(false);
    }
  }, [searchDate]);

  // DIPS認証開始
  const handleDipsAuth = async () => {
    setDipsLoading(true);
    setDipsError(null);
    try {
      const { authUrl } = await dipsApi.getAuthUrl('aircraft');
      if (!authUrl) throw new Error('認証URLが取得できませんでした');

      const handleMessage = (event) => {
        if (event.data?.type === 'DIPS_AUTH_SUCCESS') {
          dipsApi.storeToken({ accessToken: event.data.accessToken, refreshToken: event.data.refreshToken, expiresIn: event.data.expiresIn });
          setDipsAuthenticated(true);
          setDipsLoading(false);
          fetchDipsAircrafts();
          window.removeEventListener('message', handleMessage);
        } else if (event.data?.type === 'DIPS_AUTH_ERROR') {
          setDipsError('認証エラー: ' + event.data.error);
          setDipsLoading(false);
          window.removeEventListener('message', handleMessage);
        }
      };
      window.addEventListener('message', handleMessage);

      const authWindow = window.open(authUrl, 'dips_auth', 'width=600,height=700');
      if (!authWindow) { window.removeEventListener('message', handleMessage); throw new Error('ポップアップがブロックされました。'); }

      const checkAuth = setInterval(() => {
        try {
          if (authWindow?.closed) {
            clearInterval(checkAuth);
            setTimeout(() => {
              window.removeEventListener('message', handleMessage);
              if (dipsApi.isAuthenticated()) { setDipsAuthenticated(true); setDipsLoading(false); fetchDipsAircrafts(); }
              else { setDipsLoading(false); }
            }, 1000);
          }
        } catch { /* Cross-origin */ }
      }, 500);
    } catch (err) {
      setDipsError('認証エラー: ' + err.message);
      setDipsLoading(false);
    }
  };

  // 飛行計画認証開始
  const handleFlightPlanAuth = async () => {
    setFlightPlanAuthLoading(true);
    setFlightPlansError(null);
    try {
      const { authUrl } = await dipsApi.getAuthUrl('flightPlan');
      if (!authUrl) throw new Error('認証URLが取得できませんでした');

      const handleMessage = (event) => {
        if (event.data?.type === 'DIPS_AUTH_SUCCESS') {
          dipsApi.storeFlightPlanToken({ accessToken: event.data.accessToken, refreshToken: event.data.refreshToken, expiresIn: event.data.expiresIn });
          setFlightPlanAuthenticated(true);
          setFlightPlanAuthLoading(false);
          window.removeEventListener('message', handleMessage);
        } else if (event.data?.type === 'DIPS_AUTH_ERROR') {
          setFlightPlansError('認証エラー: ' + event.data.error);
          setFlightPlanAuthLoading(false);
          window.removeEventListener('message', handleMessage);
        }
      };
      window.addEventListener('message', handleMessage);

      const authWindow = window.open(authUrl, 'dips_flightplan_auth', 'width=600,height=700');
      if (!authWindow) { window.removeEventListener('message', handleMessage); throw new Error('ポップアップがブロックされました。'); }

      const checkAuth = setInterval(() => {
        try {
          if (authWindow?.closed) {
            clearInterval(checkAuth);
            setTimeout(() => {
              window.removeEventListener('message', handleMessage);
              if (dipsApi.isFlightPlanAuthenticated()) { setFlightPlanAuthenticated(true); setFlightPlanAuthLoading(false); }
              else { setFlightPlanAuthLoading(false); }
            }, 1000);
          }
        } catch { /* Cross-origin */ }
      }, 500);
    } catch (err) {
      setFlightPlansError('認証エラー: ' + err.message);
      setFlightPlanAuthLoading(false);
    }
  };

  // 範囲スキャン
  const scanDateRange = async () => {
    if (!dipsApi.isFlightPlanAuthenticated()) { setFlightPlansError('飛行計画認証が必要です'); return; }
    setScanMode(true);
    setScanResults([]);
    setFlightPlans([]);
    setFlightPlansError(null);
    const today   = new Date();
    const results = [];
    for (let i = -14; i <= 60; i++) {
      const target  = new Date(today);
      target.setDate(today.getDate() + i);
      const dateStr = target.toISOString().split('T')[0];
      setScanProgress(`検索中: ${dateStr} (${i + 15}/75)`);
      try {
        const data  = await dipsApi.getFlightPlans({
          features:      { type: 'Circle', center: [139.6917, 35.6895], radius: 500000 },
          startTime:     formatDipsDateTime(new Date(target.getFullYear(), target.getMonth(), target.getDate(), 0, 0)),
          finishTime:    formatDipsDateTime(new Date(target.getFullYear(), target.getMonth(), target.getDate(), 23, 59)),
          allFlightPlan: '1',
        });
        const plans = Array.isArray(data) ? data : data.flight_plans || data.flightPlans || data.items || data.results || [];
        if (plans.length > 0) results.push({ date: dateStr, count: plans.length, plans });
      } catch { /* 継続 */ }
      await new Promise(resolve => setTimeout(resolve, 100));
    }
    setScanProgress('');
    setScanResults(results);
    setScanMode(false);
  };

  // 飛行計画通報
  const handleReportFlightPlan = async () => {
    if (!dipsApi.isFlightPlanAuthenticated()) { setFlightPlansError('飛行計画認証が必要です'); return; }
    setReportLoading(true);
    setReportResult(null);
    setFlightPlansError(null);
    try {
      const [year, month, day] = reportForm.flightDate.split('-').map(Number);
      const [startH, startM]   = reportForm.startTime.split(':').map(Number);
      const [endH, endM]       = reportForm.endTime.split(':').map(Number);
      const result = await dipsApi.reportFlightPlan({
        features:      { type: 'Circle', center: [parseFloat(reportForm.longitude), parseFloat(reportForm.latitude)], radius: parseInt(reportForm.radius, 10) },
        startTime:     formatDipsDateTime(new Date(year, month - 1, day, startH, startM)),
        finishTime:    formatDipsDateTime(new Date(year, month - 1, day, endH, endM)),
        flightPurpose: reportForm.purpose,
        maxAltitude:   parseInt(reportForm.maxAltitude, 10),
        remarks:       reportForm.remarks || undefined,
      });
      setReportResult({ success: true, data: result });
      fetchFlightPlans();
    } catch (err) {
      setReportResult({ success: false, error: err.message });
    } finally {
      setReportLoading(false);
    }
  };

  useEffect(() => {
    if (dipsAuthenticated && !dipsAircrafts) fetchDipsAircrafts();
  }, [dipsAuthenticated, dipsAircrafts, fetchDipsAircrafts]);

  useEffect(() => {
    if (flightPlanAuthenticated && !scanMode) fetchFlightPlans();
  }, [flightPlanAuthenticated, searchDate, scanMode, fetchFlightPlans]);

  // 機体ソート（有効を先に）
  const sortedAircrafts = dipsAircrafts
    ? [...dipsAircrafts].sort((a, b) => {
        const sa = getAircraftInfo(a)?.aircraft_status;
        const sb = getAircraftInfo(b)?.aircraft_status;
        return sa === '1' && sb !== '1' ? -1 : sa !== '1' && sb === '1' ? 1 : 0;
      })
    : [];
  const activeCount   = sortedAircrafts.filter(a => getAircraftInfo(a)?.aircraft_status === '1').length;
  const inactiveCount = sortedAircrafts.length - activeCount;

  return (
    <div className="relative space-y-6">

      {/* ===== 背景右上 ぐるぐるアニメーション ===== */}
      <div className="absolute top-0 right-0 w-48 h-48 pointer-events-none opacity-20 z-0">
        <svg className="w-full h-full text-blue-500 animate-[spin_20s_linear_infinite]" viewBox="0 0 100 100">
          <circle cx="50" cy="50" fill="none" r="45" stroke="currentColor" strokeDasharray="10 5" strokeWidth="0.5" />
          <circle cx="50" cy="50" fill="none" r="35" stroke="currentColor" strokeDasharray="1 3" strokeWidth="1" />
          <path d="M 50 5 L 50 15 M 50 85 L 50 95 M 5 50 L 15 50 M 85 50 L 95 50" stroke="currentColor" strokeWidth="1" />
        </svg>
      </div>

      {/* ===== ページヘッダー ===== */}
      <div className="flex items-center justify-between border-b border-blue-900/30 pb-4">
        <div>
          <h2 className="font-label-caps text-xl font-bold tracking-[0.2em] italic text-blue-500 uppercase">
            登録機体一覧
          </h2>
        </div>
        {/* 認証状態 */}
        <div className="flex items-center gap-2">
          {dipsAuthenticated ? (
            <>
              <div className="flex items-center gap-2 bg-slate-900 border border-blue-900/40 px-3 py-1.5">
                <span className="w-2 h-2 rounded-full bg-secondary shadow-[0_0_8px_#8bd97b] animate-pulse" />
                <span className="font-label-caps text-sm text-on-surface-variant">LINKED: DIPS2.0</span>
              </div>
              <HudButton
                size="sm"
                variant="danger"
                icon="logout"
                onClick={() => {
                  dipsApi.clearToken();
                  setDipsAuthenticated(false);
                  setDipsAircrafts(null);
                  const w = window.open(
                    'https://www.dips-regdev.mlit.go.jp/auth/realms/drs-utm/protocol/openid-connect/logout',
                    'dips_logout',
                    'width=500,height=400'
                  );
                  if (w) setTimeout(() => w.close(), 500);
                }}
              >
                LOGOUT
              </HudButton>
            </>
          ) : (
            <div className="flex items-center gap-2 bg-slate-900 border border-blue-900/40 px-3 py-1.5">
              <span className="w-2 h-2 rounded-full bg-outline" />
              <span className="font-label-caps text-sm text-outline">UNLINKED: DIPS2.0</span>
            </div>
          )}
        </div>
      </div>

      {/* エラー */}
      {dipsError && <HudAlert message={dipsError} variant="error" />}

      {/* ===== 未認証状態 ===== */}
      {!dipsAuthenticated && !dipsError && !dipsLoading && (
        <div className="relative glass-panel mx-24 p-12 flex flex-col items-center text-center gap-6">
          {/* 四隅ブラケット */}
          <div className="absolute top-0 left-0 border-l-4 border-t-4 border-primary/40 w-8 h-8 pointer-events-none" />
          <div className="absolute top-0 right-0 border-r-4 border-t-4 border-primary/40 w-8 h-8 pointer-events-none" />
          <div className="absolute bottom-0 left-0 border-l-4 border-b-4 border-primary/40 w-8 h-8 pointer-events-none" />
          <div className="absolute bottom-0 right-0 border-r-4 border-b-4 border-primary/40 w-8 h-8 pointer-events-none" />

          {/* アイコン */}
          <div className="relative flex items-center justify-center w-20 h-20 border border-primary/30">
            <div className="absolute top-0 left-0 border-l-2 border-t-2 border-primary/60 w-4 h-4" />
            <div className="absolute bottom-0 right-0 border-r-2 border-b-2 border-primary/60 w-4 h-4" />
            <MSIcon name="link" className="text-4xl text-primary/50 rotate-[135deg]" />
          </div>

          {/* テキスト */}
          <div>
            <div className="bg-primary/10 py-1 px-4 inline-block mb-3">
              <span className="font-label-caps text-[11px] text-primary tracking-widest">DIPS2.0 Authentication Required</span>
            </div>
            <p className="font-body-sm text-[13px] text-on-surface-variant max-w-md">
              国土交通省のドローン情報基盤システム（DIPS2.0）と連携すると、<br />
              登録されている機体情報を表示できます。
            </p>
          </div>

          {/* 認証ボタン */}
          <HudButton icon="link" iconClassName="text-2xl rotate-[135deg]" size="lg" onClick={handleDipsAuth} loading={dipsLoading}>
            DIPS2.0認証を開始
          </HudButton>
        </div>
      )}

      {/* ===== ローディング ===== */}
      {dipsLoading && <HudSpinner label="DIPS2.0 と通信中..." />}

      {/* ===== 所有者・使用者情報 ===== */}
      {dipsAuthenticated && !dipsLoading && sortedAircrafts.length > 0 && (() => {
        const firstAircraft  = sortedAircrafts[0];
        const owner          = getOwnerInfo(firstAircraft);
        const user           = getUserInfo(firstAircraft);
        const isUserDifferent = user && user.owner_user_same_confirmation === '2';
        if (!owner) return null;
        return (
          <div className={`grid gap-4 ${isUserDifferent ? 'md:grid-cols-2' : ''}`}>
            <div className="bg-surface-container-low border border-blue-900/40 p-5"
              style={{ clipPath: 'polygon(0 0, calc(100% - 16px) 0, 100% 16px, 100% 100%, 0 100%)' }}>
              <div className="flex items-center gap-2 mb-4">
                <MSIcon name="person" className="text-base text-blue-400/70" />
                <span className="font-label-caps text-sm text-blue-400 tracking-[0.2em]">OWNER_INFO</span>
                <span className="ml-auto font-label-caps text-[13px] border border-primary/30 text-primary/70 px-2 py-0.5">
                  {OWNER_CLASSIFICATION_LABELS[owner.owner_classification] || owner.owner_classification}
                </span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <DetailRow label="氏名/法人名" value={owner.owner_fullname || owner.owner_corporation_name} />
                <DetailRow label="フリガナ"     value={owner.owner_furigana} />
                <DetailRow label="住所"         value={owner.owner_address} />
                <DetailRow label="電話番号"     value={owner.owner_tel} />
                <DetailRow label="メール"       value={owner.owner_email_address} />
                {owner.owner_birthday && <DetailRow label="生年月日" value={owner.owner_birthday} />}
              </div>
            </div>
            {isUserDifferent && (
              <div className="bg-surface-container-low border border-blue-900/40 p-5"
                style={{ clipPath: 'polygon(0 0, calc(100% - 16px) 0, 100% 16px, 100% 100%, 0 100%)' }}>
                <div className="flex items-center gap-2 mb-4">
                  <MSIcon name="group" className="text-base text-blue-400/70" />
                  <span className="font-label-caps text-sm text-blue-400 tracking-[0.2em]">USER_INFO</span>
                  <span className="ml-auto font-label-caps text-[13px] border border-primary/30 text-primary/70 px-2 py-0.5">
                    {OWNER_CLASSIFICATION_LABELS[user.user_classification] || user.user_classification}
                  </span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <DetailRow label="氏名/法人名" value={user.user_fullname || user.user_corporation_name} />
                  <DetailRow label="フリガナ"     value={user.user_furigana} />
                  <DetailRow label="住所"         value={user.user_address} />
                  <DetailRow label="電話番号"     value={user.user_tel} />
                  <DetailRow label="メール"       value={user.user_email_address} />
                  {user.user_corporation_representative_name && (
                    <DetailRow label="代表者名" value={user.user_corporation_representative_name} />
                  )}
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {/* ===== 機体グリッド ===== */}
      {dipsAuthenticated && !dipsLoading && (
        <div>
          {/* セクションヘッダー */}
          <div className="flex items-end justify-between mb-4">
            <div>
              <span className="font-label-caps text-base font-bold text-blue-500/50 tracking-widest block mb-1">機体一覧</span>
            </div>
            <div className="flex items-center gap-3">
              {activeCount > 0 && (
                <div className="flex items-center gap-2 bg-slate-900 border border-blue-900/40 px-3 py-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-secondary shadow-[0_0_6px_#8bd97b]" />
                  <span className="font-label-caps text-sm text-on-surface-variant">ACTIVE: {String(activeCount).padStart(2, '0')}</span>
                </div>
              )}
              {inactiveCount > 0 && (
                <div className="flex items-center gap-2 bg-slate-900 border border-blue-900/40 px-3 py-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-outline" />
                  <span className="font-label-caps text-sm text-on-surface-variant">INACTIVE: {String(inactiveCount).padStart(2, '0')}</span>
                </div>
              )}
            </div>
          </div>

          {sortedAircrafts.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {sortedAircrafts.map((aircraft, idx) => {
                const info     = getAircraftInfo(aircraft);
                const isActive = info.aircraft_status === '1';
                return (
                  <div
                    key={idx}
                    className={`group relative bg-surface-container-low border border-blue-900/40 p-0 hover:border-primary/60 transition-all cursor-pointer shadow-lg ${!isActive ? 'opacity-70' : ''}`}
                    style={{ clipPath: 'polygon(0 0, calc(100% - 20px) 0, 100% 20px, 100% 100%, 0 100%)' }}
                    onClick={() => setSelectedAircraft(aircraft)}
                  >
                    {/* カード上部ラベル */}
                    <div className="absolute top-2 left-3 z-10">
                      <span className="font-label-caps text-[13px] text-blue-400/70">
                        {info.registration_code || `UNIT_${String(idx + 1).padStart(2, '0')}`}
                      </span>
                    </div>

                    {/* 機体ビジュアルエリア */}
                    <div className="w-full h-32 bg-slate-950 overflow-hidden relative flex items-center justify-center">
                      <MSIcon name="flight" className="text-6xl text-primary/10 rotate-45" />
                      <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-transparent to-transparent" />
                      {/* ステータスオーバーレイ（無効時） */}
                      {!isActive && (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <span className="font-label-caps text-[13px] border border-error/60 text-error px-2 py-0.5 bg-error-container/20">
                            {info.aircraft_status === '2' ? 'EXPIRED' : 'DEREGISTERED'}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* カード情報 */}
                    <div className="p-4 space-y-3">
                      {/* 機体名・ステータス */}
                      <div className="flex justify-between items-start gap-2">
                        <div className="min-w-0">
                          <h3 className="font-headline-md text-base text-white truncate">
                            {info.model_jpn || info.model_eng || '—'}
                          </h3>
                          <p className="font-label-caps text-[13px] text-slate-500 truncate mt-0.5">
                            {info.manufacturer_jpn || info.manufacturer_eng || '—'}
                          </p>
                        </div>
                        <StatusBadge status={info.aircraft_status} />
                      </div>

                      {/* スペックグリッド */}
                      <div className="grid grid-cols-2 gap-2">
                        <div className="bg-slate-900/50 p-2 border border-blue-900/20">
                          <p className="font-label-caps text-[12px] text-slate-500">WEIGHT_KG</p>
                          <p className="font-label-caps text-[15px] text-blue-400 font-bold mt-0.5">
                            {info.aircraft_weight || '—'}
                          </p>
                        </div>
                        <div className="bg-slate-900/50 p-2 border border-blue-900/20">
                          <p className="font-label-caps text-[12px] text-slate-500">MAX_TKOFF</p>
                          <p className="font-label-caps text-[15px] text-blue-400 font-bold mt-0.5">
                            {info.maximum_takeoff_weight || '—'}
                          </p>
                        </div>
                      </div>

                      {/* 種別・登録期限 */}
                      <div className="grid grid-cols-2 gap-2">
                        <div className="bg-slate-900/50 p-2 border border-blue-900/20">
                          <p className="font-label-caps text-[12px] text-slate-500">TYPE</p>
                          <p className="font-label-caps text-[13px] text-blue-400 mt-0.5 leading-tight">
                            {AIRCRAFT_TYPE_LABELS[info.aircraft_type]?.replace('（', '\n（') || info.aircraft_type || '—'}
                          </p>
                        </div>
                        <div className="bg-slate-900/50 p-2 border border-blue-900/20">
                          <p className="font-label-caps text-[12px] text-slate-500">EXPIRES</p>
                          <p className="font-label-caps text-[13px] text-blue-400 font-bold mt-0.5">
                            {formatDate(info.effectiveness_period_to)}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* ホバー時の右下矢印 */}
                    <div className="absolute bottom-0 right-0 w-8 h-8 bg-primary/20 flex items-center justify-center scale-0 group-hover:scale-100 transition-transform"
                      style={{ clipPath: 'polygon(0 0, 100% 0, 100% 100%)' }}>
                      <MSIcon name="arrow_forward_ios" className="text-primary text-sm" />
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="glass-panel flex flex-col items-center justify-center py-16 gap-4 text-center">
              <MSIcon name="flight_off" className="text-5xl text-primary/20" />
              <span className="font-label-caps text-[15px] text-slate-500 tracking-[0.2em]">NO AIRCRAFT REGISTERED</span>
            </div>
          )}
        </div>
      )}

      {/* ===== 飛行計画セクション ===== */}
      {dipsAuthenticated && !dipsLoading && (
        <div className="bg-surface-container-low border border-blue-900/40"
          style={{ clipPath: 'polygon(0 0, calc(100% - 20px) 0, 100% 20px, 100% 100%, 0 100%)' }}>

          {/* セクションヘッダー */}
          <div className="flex items-center justify-between px-5 py-3 border-b border-blue-900/40">
            <div className="flex items-center gap-3">
              <MSIcon name="calendar_month" className="text-base text-blue-400/70" />
              <span className="font-label-caps text-[15px] text-blue-400 tracking-[0.2em]">FLIGHT_PLANS</span>
              {flightPlanAuthenticated ? (
                <span className="font-label-caps text-[13px] border border-secondary/50 text-secondary px-2 py-0.5">AUTH_OK</span>
              ) : (
                <span className="font-label-caps text-[13px] border border-outline/40 text-outline px-2 py-0.5">AUTH_REQUIRED</span>
              )}
            </div>
            {flightPlanAuthenticated && (
              <div className="flex items-center gap-2">
                <input
                  type="date"
                  value={searchDate}
                  onChange={(e) => setSearchDate(e.target.value)}
                  disabled={scanMode}
                  className="bg-slate-900 border border-blue-900/50 text-on-surface font-label-caps text-[15px] px-2 py-1 focus:outline-none focus:border-primary/60 rounded-none"
                />
                <HudButton size="sm" variant="secondary" icon="refresh" onClick={() => { setScanResults([]); fetchFlightPlans(); }} disabled={flightPlansLoading || scanMode}>
                  検索
                </HudButton>
                <HudButton size="sm" variant="secondary" icon="radar" onClick={scanDateRange} disabled={flightPlansLoading || scanMode}>
                  {scanMode ? scanProgress : '範囲スキャン'}
                </HudButton>
                <HudButton size="sm" variant="primary" icon="add" onClick={() => setShowReportForm(!showReportForm)}>
                  飛行計画通報
                </HudButton>
              </div>
            )}
          </div>

          <div className="p-5">
            {/* 飛行計画認証 */}
            {!flightPlanAuthenticated && (
              <div className="flex flex-col items-center py-10 gap-4 text-center">
                <MSIcon name="lock" className="text-4xl text-primary/20" />
                <p className="font-label-caps text-sm text-slate-500 tracking-[0.15em]">
                  飛行計画の表示には別途認証が必要です
                </p>
                <HudButton icon="key" size="wide" onClick={handleFlightPlanAuth} loading={flightPlanAuthLoading}>
                  飛行計画認証
                </HudButton>
              </div>
            )}

            {/* 飛行計画通報フォーム */}
            {showReportForm && flightPlanAuthenticated && (
              <div className="mb-5 border border-blue-900/40 bg-slate-900/30 p-4">
                <div className="flex items-center gap-2 mb-4">
                  <MSIcon name="add_circle" className="text-base text-primary/70" />
                  <span className="font-label-caps text-sm text-primary tracking-[0.2em]">NEW_FLIGHT_PLAN</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {[
                    { label: '飛行日', field: 'flightDate', type: 'date' },
                    { label: '開始時刻', field: 'startTime', type: 'time' },
                    { label: '終了時刻', field: 'endTime', type: 'time' },
                    { label: '緯度', field: 'latitude', type: 'text', placeholder: '例: 35.6895' },
                    { label: '経度', field: 'longitude', type: 'text', placeholder: '例: 139.6917' },
                    { label: '半径 (m)', field: 'radius', type: 'number', placeholder: '500' },
                    { label: '最大高度 (m)', field: 'maxAltitude', type: 'number', placeholder: '150' },
                    { label: '備考', field: 'remarks', type: 'text', placeholder: '任意' },
                  ].map(({ label, field, type, placeholder }) => (
                    <div key={field}>
                      <label className="font-label-caps text-[13px] text-slate-500 tracking-widest block mb-1">{label}</label>
                      <input
                        type={type}
                        value={reportForm[field]}
                        onChange={(e) => setReportForm({ ...reportForm, [field]: e.target.value })}
                        placeholder={placeholder}
                        className="w-full bg-slate-950 border border-blue-900/40 text-on-surface font-body-sm text-base px-3 py-1.5 focus:outline-none focus:border-primary/60 rounded-none"
                      />
                    </div>
                  ))}
                  <div>
                    <label className="font-label-caps text-[9px] text-slate-500 tracking-widest block mb-1">飛行目的</label>
                    <select
                      value={reportForm.purpose}
                      onChange={(e) => setReportForm({ ...reportForm, purpose: e.target.value })}
                      className="w-full bg-slate-950 border border-blue-900/40 text-on-surface font-body-sm text-[13px] px-3 py-1.5 focus:outline-none focus:border-primary/60 rounded-none"
                    >
                      {['空撮','測量','点検','農薬散布','物資輸送','趣味','その他'].map(v => <option key={v} value={v}>{v}</option>)}
                    </select>
                  </div>
                </div>
                <div className="mt-4 flex items-center gap-3">
                  <HudButton icon="send" onClick={handleReportFlightPlan} loading={reportLoading}>
                    飛行計画を通報
                  </HudButton>
                  <HudButton variant="secondary" onClick={() => setShowReportForm(false)}>
                    キャンセル
                  </HudButton>
                </div>
                {reportResult && (
                  <div className="mt-4">
                    {reportResult.success
                      ? <HudAlert message="飛行計画を通報しました" variant="success" />
                      : <HudAlert message={`エラー: ${reportResult.error}`} variant="error" />
                    }
                  </div>
                )}
              </div>
            )}

            {/* 飛行計画エラー */}
            {flightPlansError && <HudAlert message={`飛行計画の取得に失敗しました: ${flightPlansError}`} variant="warning" />}

            {/* スキャン中 */}
            {scanMode && (
              <div className="flex flex-col items-center py-10 gap-3">
                <div className="relative w-10 h-10">
                  <div className="absolute inset-0 border-2 border-primary/20" />
                  <div className="absolute inset-0 border-t-2 border-primary animate-spin" />
                </div>
                <span className="font-label-caps text-[15px] text-primary/70 tracking-[0.15em]">{scanProgress}</span>
                <span className="font-label-caps text-sm text-slate-500">過去2週間〜未来2ヶ月をスキャン中...</span>
              </div>
            )}

            {/* フライトプランローディング */}
            {!scanMode && flightPlansLoading && <HudSpinner label="飛行計画を取得中..." />}

            {/* スキャン結果 */}
            {!scanMode && !flightPlansLoading && scanResults.length > 0 && (
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-[15px] pb-2 border-b border-blue-900/30">
                  <MSIcon name="check_circle" className="text-base text-secondary" />
                  <span className="font-label-caps text-secondary tracking-wider">
                    SCAN_COMPLETE: {scanResults.reduce((s, r) => s + r.count, 0)}件 / {scanResults.length}日
                  </span>
                </div>
                {scanResults.map((result, idx) => (
                  <div key={idx} className="border border-blue-900/30 bg-slate-900/30">
                    <div className="flex items-center justify-between px-4 py-2 border-b border-blue-900/20">
                      <span className="font-label-caps text-[15px] text-on-surface">{result.date}</span>
                      <span className="font-label-caps text-[13px] border border-tertiary/40 text-tertiary px-2 py-0.5">{result.count}件</span>
                    </div>
                    <div className="divide-y divide-blue-900/20">
                      {result.plans.map((plan, pIdx) => (
                        <div key={pIdx} className="flex items-center justify-between px-4 py-2 hover:bg-blue-900/10 cursor-pointer transition-colors" onClick={() => setSelectedFlightPlan(plan)}>
                          <span className="font-body-sm text-base text-on-surface-variant">
                            {plan.flight_purpose || plan.purpose || plan.flightPurpose || '飛行計画'}
                          </span>
                          <MSIcon name="chevron_right" className="text-sm text-slate-500" />
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* 単日検索結果 */}
            {!scanMode && !flightPlansLoading && scanResults.length === 0 && flightPlans.length > 0 && (
              <div className="divide-y divide-blue-900/20">
                {flightPlans.map((plan, idx) => (
                  <div key={idx} className="flex items-center justify-between px-1 py-3 hover:bg-blue-900/10 cursor-pointer transition-colors" onClick={() => setSelectedFlightPlan(plan)}>
                    <div>
                      <p className="font-label-caps text-[15px] text-on-surface tracking-wider">
                        {plan.flight_purpose || plan.purpose || '飛行計画'}
                      </p>
                      <p className="font-body-sm text-[12px] text-slate-500 mt-0.5">
                        {formatDate(plan.flight_start_datetime || plan.start_date)} ～ {formatDate(plan.flight_end_datetime || plan.end_date)}
                      </p>
                    </div>
                    <MSIcon name="chevron_right" className="text-sm text-slate-500" />
                  </div>
                ))}
              </div>
            )}

            {/* 空状態 */}
            {flightPlanAuthenticated && !scanMode && !flightPlansLoading && scanResults.length === 0 && flightPlans.length === 0 && !flightPlansError && (
              <div className="flex flex-col items-center py-10 gap-3 text-center">
                <MSIcon name="event_busy" className="text-4xl text-primary/20" />
                <p className="font-label-caps text-sm text-slate-500 tracking-[0.15em]">
                  {searchDate} の飛行計画はありません
                </p>
                <p className="font-label-caps text-[13px] text-slate-600">「範囲スキャン」で広範囲を検索できます</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ===== 機体詳細モーダル ===== */}
      <AircraftDetailModal
        aircraft={selectedAircraft}
        onClose={() => setSelectedAircraft(null)}
      />

      {/* ===== 飛行計画詳細モーダル ===== */}
      <HudModal
        isOpen={!!selectedFlightPlan}
        onClose={() => setSelectedFlightPlan(null)}
        title="FLIGHT_PLAN_DETAIL"
      >
        {selectedFlightPlan && (
          <div className="bg-slate-950 border border-blue-900/20 p-4 max-h-80 overflow-auto">
            <pre className="font-body-sm text-[15px] text-slate-400 whitespace-pre-wrap">
              {JSON.stringify(selectedFlightPlan, null, 2)}
            </pre>
          </div>
        )}
      </HudModal>
    </div>
  );
}
